import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  CLAIM_GROUP_FIELD_INITIAL_VALUES,
  claimGroupFieldValidationSchema,
  fetchClaimGroupById,
  getClaimNameDdl,
  handleSubmitClaimGroup,
  IClaimGroupForm,
} from "./CliamGroupFormController";
import {
  getLocalDate,
  handleError,
  prepareMessageFromParams,
  sweetAlertError,
} from "../../utils/AppFunctions";
import { MESSAGE_UNKNOWN_ERROR_OCCURRED } from "../../utils/AppConstants";
import {
  AdvanceSearch,
  ICButton,
  ICConfirmationModal,
} from "../../core-component/ui";
import { Form, Formik } from "formik";
import { ICFormikSwitch, ICFormikTextInput } from "../../core-component/formik";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import Loader from "../../core-component/ui/Loader";
import { IClaims, IOption } from "../../data/AppInterface";
import { ICTextInput } from "../../core-component/form";
import {
  closeIcon,
  deleteIcon,
  searchIcon,
} from "../../assets/icons/SvgIconList";
import { routeAdmin, routeMaster } from "../../routes/AuthRoutePath";
import { cn } from "../../lib";

interface IClaimGroupFormProps {
  handleClose?: (status: boolean) => void;
  readOnly?: boolean;
}

const ClaimGroupForm: React.FC<IClaimGroupFormProps> = ({
  handleClose,
  readOnly,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: claimNameToEdit } = useParams();
  const backTo: string =
    location.state?.backTo ?? `/${routeMaster.admin}/${routeAdmin.claimGroup}`;
  const goBack = () => navigate(backTo);

  const { t } = UseTranslationHook();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const [claimGroupFormInitialValues, setClaimGroupFormInitialValues] =
    useState<IClaimGroupForm | undefined>(CLAIM_GROUP_FIELD_INITIAL_VALUES);
  const [claimGroupName, setClaimGroupName] = useState<IOption<string>[]>([]);
  const [showClaims, setShowClaims] = useState(false);
  const [addClaims, setAddClaims] = useState<IClaims[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [enterpriseSearch, setEnterpriseSearch] = useState<string>("");
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (claimNameToEdit) {
      getClaimGroupById(claimNameToEdit);
    } else {
      setClaimGroupFormInitialValues(CLAIM_GROUP_FIELD_INITIAL_VALUES);
    }
    fetchClaimName();
  }, [claimNameToEdit]);

  const getClaimGroupById = (id: string) => {
    setLoading(true);
    setTimeout(async () => {
      await fetchClaimGroupById(
        id,
        handleClose ?? goBack,
        setClaimGroupFormInitialValues,
        setAddClaims,
      );
      setLoading(false);
    }, 500);
  };

  const fetchClaimName = () => {
    setLoading(true);
    setTimeout(async () => {
      await getClaimNameDdl(setClaimGroupName);
      setLoading(false);
    }, 500);
  };

  const handleSubmit = async (values: IClaimGroupForm) => {
    if (addClaims.length === 0) {
      sweetAlertError({
        message: prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("claims")],
        ]),
      });
      return;
    }
    try {
      setSubmitLoading(true);
      await handleSubmitClaimGroup(
        {
          ...values,
          claims: addClaims.map((values) => values.id),
        },
        setSubmitLoading,
        claimNameToEdit,
        handleClose ?? goBack,
      );
    } catch (e: any) {
      sweetAlertError({ message: MESSAGE_UNKNOWN_ERROR_OCCURRED });
      handleError(e, "handleSubmit");
    } finally {
      setSubmitLoading(false);
    }
  };
  const handleOutsideClickEvent = () => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowClaims(false);
        setEnterpriseSearch("");
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowClaims(true);
        setEnterpriseSearch("");
      }
    };
    if (showClaims) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    };
  };
  useEffect(() => {
    handleOutsideClickEvent();
    setIsVisible(true);
  }, [showClaims]);

  const handleRemoveClaim = (claimId: string) => {
    setAddClaims((prevClaims) =>
      prevClaims.filter((claim) => claim.id !== claimId),
    );
  };
  const addClaim = (item: IOption<string>) => {
    setAddClaims((prevClaims) => [
      ...prevClaims,
      {
        id: item.data_value,
        claim_name: item.display_value,
      },
    ]);
  };
  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className="flex flex-col overflow-y-hidden flex-1 p-4">
          <AdvanceSearch
            backButton
            title={
              claimGroupFormInitialValues?.claim_group_name
                ? `${t("claim_group")} (${
                    claimGroupFormInitialValues.claim_group_name
                  })`
                : t("claim_group")
            }
            createMassage={
              claimNameToEdit
                ? {
                    name: claimGroupFormInitialValues?.created_by ?? "",
                    date: getLocalDate(
                      claimGroupFormInitialValues?.created_date,
                    ),
                  }
                : undefined
            }
            modifiedMassage={
              claimNameToEdit && claimGroupFormInitialValues?.modified_date
                ? {
                    name: claimGroupFormInitialValues?.modified_by ?? "",
                    date: getLocalDate(
                      claimGroupFormInitialValues?.modified_date,
                    ),
                  }
                : undefined
            }
          />
          {claimGroupFormInitialValues && (
            <Formik
              initialValues={claimGroupFormInitialValues}
              validationSchema={claimGroupFieldValidationSchema(t)}
              onSubmit={(values) => handleSubmit(values)}
            >
              {(formikProps) => {
                const { handleSubmit } = formikProps;
                return (
                  <Form
                    onSubmit={handleSubmit}
                    className="flex flex-col h-full bg-subtle rounded-md p-4 mt-4 overflow-y-hidden"
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                      <ICFormikTextInput
                        {...formikProps}
                        fieldName="claim_group_name"
                        placeholder={t("claim_group_name")}
                        required
                        tabIndex={0}
                        disabled={readOnly}
                        autoFocus={true}
                      />
                      <ICFormikSwitch
                        {...formikProps}
                        name="is_active"
                        containerClassName="gap-4 !w-fit"
                        placeholder={t("status")}
                        checkedValue={BIT_VALUE.One}
                        unCheckedValue={BIT_VALUE.Zero}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="relative w-full mb-4 col-span-2">
                      <div
                        className="custom-text-input-container w-full flex items-center justify-start space-x-3 cursor-pointer border rounded-md"
                        onClick={
                          showClaims === false
                            ? () => setShowClaims(true)
                            : () => {}
                        }
                      >
                        <div className="size-5">{searchIcon}</div>
                        <div>{t("search_to_claim_add_more")}</div>
                      </div>

                      {showClaims && (
                        <div
                          ref={dropdownRef}
                          className={cn(
                            `fixed inset-0 z-50 backdrop-blur-sm flex items-center justify-center bg-black bg-opacity-40 transition-opacity duration-300`,
                            {
                              "opacity-100": isVisible,
                              "opacity-0": !isVisible,
                            },
                          )}
                        >
                          <div className="p-4 bg-subtle rounded-lg shadow-lg w-10/12 h-3/4 mx-4 md:mx-0 custom-transform flex flex-col">
                            <div className="flex justify-between">
                              <h2 className="font-semibold">{t("claims")}</h2>
                              <div
                                className="size-5 cursor-pointer"
                                onClick={() => {
                                  setIsVisible(false);
                                  setShowClaims(false);
                                }}
                              >
                                {closeIcon}
                              </div>
                            </div>

                            <div className="pt-4">
                              <ICTextInput
                                name="claims"
                                leading={
                                  <div className="size-5">{searchIcon}</div>
                                }
                                value={enterpriseSearch}
                                onChange={(e) =>
                                  setEnterpriseSearch(e.target.value)
                                }
                                placeholder={t("search_to_claim_add_more")}
                                containerClassName="w-full flex items-center justify-start space-x-3"
                              />
                            </div>

                            <div className="my-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-y-scroll ">
                              {claimGroupName
                                .filter((item) =>
                                  item.display_value
                                    .toLowerCase()
                                    .includes(enterpriseSearch.toLowerCase()),
                                )
                                .map((item, index) => {
                                  const isSelected = addClaims.some(
                                    (claim) => claim.id === item.data_value,
                                  );
                                  return (
                                    <div
                                      key={item.data_value}
                                      className={cn(
                                        "p-2 rounded-md flex justify-between",
                                        {
                                          "bg-accent-muted": isSelected,
                                          "hover:bg-base": !isSelected,
                                        },
                                      )}
                                      onClick={() => {
                                        if (!isSelected) {
                                          addClaim(item);
                                        }
                                      }}
                                    >
                                      <div>{item.display_value}</div>
                                      {isSelected && (
                                        <div
                                          className="size-5 cursor-pointer"
                                          onClick={() =>
                                            handleRemoveClaim(
                                              item.data_value,
                                            )
                                          }
                                        >
                                          {deleteIcon}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              {enterpriseSearch &&
                                claimGroupName.filter((item) =>
                                  item.display_value
                                    .toLowerCase()
                                    .includes(enterpriseSearch.toLowerCase()),
                                ).length === 0 && (
                                  <div className="flex justify-center col-span-4 font-semibold">
                                    {t("record_not_found")}
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 flex-col overflow-y-scroll mb-4">
                      <div className=" w-full max-h-full  grid grid-cols-2 lg:grid-cols-4 gap-4 pb-4">
                        {addClaims.length > 0 &&
                          addClaims.map((item, index) => (
                            <div
                              key={item.id}
                              className="flex justify-between bg-accent-muted rounded-md items-center p-2 "
                            >
                              <div>{item.claim_name}</div>
                              <div
                                className="size-5 cursor-pointer"
                                onClick={() => handleRemoveClaim(item.id)}
                              >
                                {deleteIcon}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                    <div>
                      {!readOnly && (
                        <ICButton
                          loading={submitLoading}
                          className="w-full"
                          children={t("submit")}
                          disabled={submitLoading}
                        />
                      )}
                    </div>
                  </Form>
                );
              }}
            </Formik>
          )}

          {showDiscardWarning && (
            <ICConfirmationModal
              title={t("discard_warning")}
              message={t("discard_unsaved_changes")}
              onClose={(value) => {
                setShowDiscardWarning(false);
                value && handleClose?.(false);
              }}
            />
          )}
        </div>
      )}
    </>
  );
};

export default ClaimGroupForm;
